import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { LOAD_CAPACITY, makeDefaultTables, overlapIds } from "../lib/calc";
import { printDaySchedule } from "../lib/daySchedule";
import { packYard } from "../lib/packYard";
import { Navbar } from "../components/Navbar";
import { PanelQueue } from "../components/PanelQueue";
import { PanelForm } from "../components/PanelForm";
import { YardPlanner } from "../components/YardPlanner";
import { LoadPlanner } from "../components/LoadPlanner";
import { HistoryCalendar } from "../components/HistoryCalendar";
import { TableEditor } from "../components/TableEditor";
import { ImportReview } from "../components/ImportReview";
import { DrawingBank } from "../components/DrawingBank";
import { BankPicker } from "../components/BankPicker";
import { OverviewBoard } from "../components/OverviewBoard";
import * as offline from "../lib/offline";
import { X, Plus, Undo2, Redo2, Printer } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const makeDefaultLoads = () =>
  Array.from({ length: 15 }, (_, i) => ({ id: `load-${i + 1}`, label: `Load ${i + 1}`, panelIds: [] }));
const MO = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const shortDate = (d) => {
  const [, m, day] = d.split("-");
  return `${MO[+m - 1]} ${+day}`;
};
const EMPTY = { panels: [], placements: [], loads: [], tables: [], yardCols: 1 };
const normDay = (data) => ({
  panels: data?.panels || [],
  placements: data?.placements || [],
  loads: data?.loads && data.loads.length ? data.loads : makeDefaultLoads(),
  tables: data?.tables && data.tables.length ? data.tables : makeDefaultTables(),
  yardCols: data?.yardCols || 1,
});

export default function Dashboard() {
  const [days, setDays] = useState({});
  const [order, setOrder] = useState([]);
  const [activeDate, setActiveDate] = useState(todayStr());
  const [showForm, setShowForm] = useState(false);
  const [editingPanel, setEditingPanel] = useState(null);
  const [showTableEditor, setShowTableEditor] = useState(false);
  const [reviewPanels, setReviewPanels] = useState(null);
  const [showBankPicker, setShowBankPicker] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [importing, setImporting] = useState(false);
  const [saveState, setSaveState] = useState("idle");
  const [view, setView] = useState("planner");

  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const onlineRef = useRef(online);
  onlineRef.current = online;
  const [pending, setPending] = useState(offline.queueCount());
  const refreshPending = useCallback(() => setPending(offline.queueCount()), []);

  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const activeProjectIdRef = useRef(null);
  activeProjectIdRef.current = activeProjectId;

  const daysRef = useRef(days);
  daysRef.current = days;
  const dirtyRef = useRef(new Set());
  const [dirtyDates, setDirtyDates] = useState(() => new Set());
  const saveTimer = useRef(null);
  const dragTab = useRef(null);
  const [dragOverTab, setDragOverTab] = useState(null);

  // ---- undo / redo history (per day) ----
  const [hist, setHist] = useState({}); // date -> { past: [], future: [] }
  const histRef = useRef(hist);
  histRef.current = hist;

  // ---- sync engine: flush queued writes to the server (last-write-wins, no conflict prompt) ----
  const syncQueue = useCallback(async () => {
    if (!onlineRef.current) return;
    const q = offline.readQueue();
    if (!q.length) return;
    setSaveState("saving");
    for (const { pid, date } of q) {
      const cached = offline.readDay(pid, date);
      if (!cached) {
        offline.dequeue(pid, date);
        continue;
      }
      // Auto-resolve: keep this device's changes (last-write-wins). No conflict prompt.
      try {
        const { data } = await api.put(`/snapshots/${date}`, cached.day, { params: { projectId: pid } });
        offline.setBase(pid, date, data.savedAt);
        offline.dequeue(pid, date);
      } catch (e) {
        if (offline.isNetworkError(e)) {
          setSaveState("offline");
          setOnline(false);
          refreshPending();
          return;
        }
      }
    }
    refreshPending();
    setSaveState(offline.queueCount() === 0 ? "saved" : "idle");
  }, [refreshPending]);

  // ---- initial load (offline-aware) ----
  useEffect(() => {
    (async () => {
      const today = todayStr();
      let pid = null;
      try {
        const { data: pdata } = await api.get("/projects");
        const projs = pdata.projects || [];
        offline.cacheProjects(projs);
        setProjects(projs);
        pid = projs[0]?.id || null;
        setActiveProjectId(pid);
      } catch (e) {
        const cached = offline.readProjects();
        if (cached.length) {
          setProjects(cached);
          pid = cached[0]?.id || null;
          setActiveProjectId(pid);
          setOnline(false);
        }
      }
      let day = null;
      try {
        const { data } = await api.get(`/snapshots/${today}`, { params: { projectId: pid } });
        day = normDay(data);
        offline.cacheDay(pid, today, day, data.savedAt || null);
      } catch (e) {
        if (offline.isNetworkError(e)) setOnline(false);
        const cached = offline.readDay(pid, today);
        day = cached ? normDay(cached.day) : normDay(null);
      }
      setDays({ [today]: day });
      setOrder([today]);
      setActiveDate(today);
      setLoaded(true);
      if (navigator.onLine) syncQueue();
    })();
  }, [syncQueue]);

  // ---- persist dirty days locally, then sync if online ----
  const flushDirty = useCallback(async () => {
    const dates = [...dirtyRef.current];
    dirtyRef.current.clear();
    if (!dates.length) return;
    const pid = activeProjectIdRef.current;
    dates.forEach((d) => {
      const day = daysRef.current[d];
      if (!day) return;
      const existing = offline.readDay(pid, d);
      offline.cacheDay(pid, d, day, existing ? existing.base : null);
      offline.enqueue(pid, d);
    });
    setDirtyDates((prev) => {
      const n = new Set(prev);
      dates.forEach((d) => n.delete(d));
      return n;
    });
    refreshPending();
    if (onlineRef.current) {
      await syncQueue();
    } else {
      setSaveState("offline");
    }
  }, [syncQueue, refreshPending]);

  // ---- online / offline listeners ----
  useEffect(() => {
    const goOnline = () => { setOnline(true); onlineRef.current = true; syncQueue(); };
    const goOffline = () => { setOnline(false); onlineRef.current = false; setSaveState("offline"); };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [syncQueue]);

  useEffect(() => {
    if (!loaded) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flushDirty, 900);
    return () => clearTimeout(saveTimer.current);
  }, [days, loaded, flushDirty]);

  const active = days[activeDate] || EMPTY;
  const { panels, placements, loads, tables, yardCols } = active;
  const panelsById = useMemo(() => Object.fromEntries((panels || []).map((p) => [p.id, p])), [panels]);
  const projectName = useMemo(() => projects.find((p) => p.id === activeProjectId)?.name || "", [projects, activeProjectId]);

  const markDirty = useCallback((date) => {
    dirtyRef.current.add(date);
    setDirtyDates((s) => (s.has(date) ? s : new Set(s).add(date)));
  }, []);

  const patchActive = useCallback(
    (key, updater) =>
      setDays((prev) => {
        const d = prev[activeDate];
        if (!d) return prev;
        const next = typeof updater === "function" ? updater(d[key]) : updater;
        return { ...prev, [activeDate]: { ...d, [key]: next } };
      }),
    [activeDate]
  );

  // snapshot current active state into the undo stack (call BEFORE a mutation)
  const pushHistory = useCallback(() => {
    const cur = daysRef.current[activeDate];
    if (!cur) return;
    const snap = { panels: cur.panels, placements: cur.placements, loads: cur.loads, tables: cur.tables, yardCols: cur.yardCols };
    setHist((prev) => {
      const h = prev[activeDate] || { past: [], future: [] };
      return { ...prev, [activeDate]: { past: [...h.past, snap].slice(-60), future: [] } };
    });
    markDirty(activeDate);
  }, [activeDate, markDirty]);

  const undo = useCallback(() => {
    const h = histRef.current[activeDate];
    if (!h || !h.past.length) return;
    const cur = daysRef.current[activeDate];
    const curSnap = { panels: cur.panels, placements: cur.placements, loads: cur.loads, tables: cur.tables, yardCols: cur.yardCols };
    const restore = h.past[h.past.length - 1];
    setDays((d) => ({ ...d, [activeDate]: { ...d[activeDate], ...restore } }));
    setHist((prev) => ({ ...prev, [activeDate]: { past: h.past.slice(0, -1), future: [...h.future, curSnap] } }));
    markDirty(activeDate);
  }, [activeDate, markDirty]);

  const redo = useCallback(() => {
    const h = histRef.current[activeDate];
    if (!h || !h.future.length) return;
    const cur = daysRef.current[activeDate];
    const curSnap = { panels: cur.panels, placements: cur.placements, loads: cur.loads, tables: cur.tables, yardCols: cur.yardCols };
    const restore = h.future[h.future.length - 1];
    setDays((d) => ({ ...d, [activeDate]: { ...d[activeDate], ...restore } }));
    setHist((prev) => ({ ...prev, [activeDate]: { past: [...h.past, curSnap], future: h.future.slice(0, -1) } }));
    markDirty(activeDate);
  }, [activeDate, markDirty]);

  const canUndo = (hist[activeDate]?.past?.length || 0) > 0;
  const canRedo = (hist[activeDate]?.future?.length || 0) > 0;

  // keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      if (e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.key.toLowerCase() === "z" && e.shiftKey) || e.key.toLowerCase() === "y") {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  const setPanels = useCallback((u) => { patchActive("panels", u); markDirty(activeDate); }, [patchActive, markDirty, activeDate]);
  const setPlacements = useCallback((u) => { patchActive("placements", u); markDirty(activeDate); }, [patchActive, markDirty, activeDate]);
  const setLoads = useCallback((u) => { patchActive("loads", u); markDirty(activeDate); }, [patchActive, markDirty, activeDate]);
  const setTables = useCallback((u) => { patchActive("tables", u); markDirty(activeDate); }, [patchActive, markDirty, activeDate]);
  const setYardCols = useCallback((u) => { patchActive("yardCols", u); markDirty(activeDate); }, [patchActive, markDirty, activeDate]);

  // ---- day open / close ----
  const openDate = async (date) => {
    if (days[date]) {
      setActiveDate(date);
      setView("planner");
      return;
    }
    let dayData;
    const pid = activeProjectIdRef.current;
    try {
      const { data } = await api.get(`/snapshots/${date}`, { params: { projectId: pid } });
      dayData = normDay(data);
      offline.cacheDay(pid, date, dayData, data.savedAt || null);
      toast.success(`Opened ${date}`);
    } catch (e) {
      if (e.response?.status === 404) {
        dayData = normDay(null);
        toast.info(`New plan started for ${date}`);
      } else if (offline.isNetworkError(e)) {
        setOnline(false);
        const cached = offline.readDay(pid, date);
        if (cached) {
          dayData = normDay(cached.day);
          toast.info(`Opened ${date} (offline)`);
        } else {
          toast.error("That day isn't available offline");
          return;
        }
      } else {
        toast.error("Could not open that date");
        return;
      }
    }
    setDays((prev) => ({ ...prev, [date]: dayData }));
    setOrder((prev) => (prev.includes(date) ? prev : [...prev, date]));
    setActiveDate(date);
    setView("planner");
  };

  const closeDay = (e, date) => {
    e.stopPropagation();
    if (order.length <= 1) return;
    const idx = order.indexOf(date);
    const newOrder = order.filter((d) => d !== date);
    setOrder(newOrder);
    setDays((prev) => {
      const c = { ...prev };
      delete c[date];
      return c;
    });
    setHist((prev) => {
      const c = { ...prev };
      delete c[date];
      return c;
    });
    dirtyRef.current.delete(date);
    setDirtyDates((s) => {
      if (!s.has(date)) return s;
      const n = new Set(s);
      n.delete(date);
      return n;
    });
    if (activeDate === date) setActiveDate(newOrder[Math.max(0, idx - 1)]);
  };

  const reorderTabs = (from, to) => {
    if (from === to) return;
    setOrder((prev) => {
      const arr = [...prev];
      const fi = arr.indexOf(from);
      const ti = arr.indexOf(to);
      if (fi < 0 || ti < 0) return prev;
      arr.splice(fi, 1);
      arr.splice(ti, 0, from);
      return arr;
    });
  };

  // ---- Projects (yards) ----
  const loadProjects = useCallback(async () => {
    const { data } = await api.get("/projects");
    const projs = data.projects || [];
    offline.cacheProjects(projs);
    setProjects(projs);
    return projs;
  }, []);

  const switchProject = useCallback(async (pid) => {
    if (!pid || pid === activeProjectIdRef.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    await flushDirty(); // persist any pending changes to the CURRENT project first
    activeProjectIdRef.current = pid;
    setActiveProjectId(pid);
    setHist({});
    dirtyRef.current.clear();
    setDirtyDates(new Set());
    const today = todayStr();
    let day = null;
    try {
      const { data } = await api.get(`/snapshots/${today}`, { params: { projectId: pid } });
      day = normDay(data);
      offline.cacheDay(pid, today, day, data.savedAt || null);
    } catch (e) {
      if (offline.isNetworkError(e)) setOnline(false);
      const cached = offline.readDay(pid, today);
      day = cached ? normDay(cached.day) : normDay(null);
    }
    setDays({ [today]: day });
    setOrder([today]);
    setActiveDate(today);
    setView("planner");
  }, [flushDirty]);

  const createProject = useCallback(async (name) => {
    try {
      const { data } = await api.post("/projects", { name });
      await loadProjects();
      await switchProject(data.id);
      toast.success(`Created yard "${data.name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create yard");
    }
  }, [loadProjects, switchProject]);

  const renameProject = useCallback(async (id, name) => {
    try {
      await api.patch(`/projects/${id}`, { name });
      await loadProjects();
      toast.success("Yard renamed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
    }
  }, [loadProjects]);

  const deleteProject = useCallback(async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      const remaining = await loadProjects();
      toast.success("Yard deleted");
      if (id === activeProjectIdRef.current && remaining[0]) {
        activeProjectIdRef.current = null; // force switch
        await switchProject(remaining[0].id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  }, [loadProjects, switchProject]);

  // ---- Panel CRUD ----
  const addPanel = (panel) => {
    pushHistory();
    setPanels((prev) => [panel, ...prev]);
    setShowForm(false);
    toast.success(`${panel.name} added to queue`);
  };
  const deletePanel = (id) => {
    pushHistory();
    setPanels((prev) => prev.filter((p) => p.id !== id));
    setPlacements((prev) => prev.filter((pl) => pl.panelId !== id));
    setLoads((prev) => prev.map((l) => ({ ...l, panelIds: l.panelIds.filter((pid) => pid !== id) })));
  };
  const updatePanel = (updated) => {
    pushHistory();
    setPanels((prev) => prev.map((p) => (p.id === updated.id ? { ...updated, dimsEstimated: false } : p)));
    setEditingPanel(null);
    toast.success(`${updated.name} updated`);
  };

  const applyReviewedDims = (updates) => {
    pushHistory();
    const byId = Object.fromEntries(updates.map((u) => [u.id, u]));
    setPanels((prev) =>
      prev.map((p) =>
        byId[p.id] ? { ...p, width: byId[p.id].width, length: byId[p.id].length, dimsEstimated: false } : p
      )
    );
    setReviewPanels(null);
    toast.success("Panel dimensions confirmed");
  };

  const clearPanels = (job) => {
    pushHistory();
    if (job) {
      const ids = new Set(panels.filter((p) => (p.jobNumber || "No Job #") === job).map((p) => p.id));
      setPanels((prev) => prev.filter((p) => !ids.has(p.id)));
      setPlacements((prev) => prev.filter((pl) => !ids.has(pl.panelId)));
      setLoads((prev) => prev.map((l) => ({ ...l, panelIds: l.panelIds.filter((pid) => !ids.has(pid)) })));
    } else {
      setPanels([]);
      setPlacements([]);
      setLoads((prev) => prev.map((l) => ({ ...l, panelIds: [] })));
    }
    toast.success(job ? `Cleared job ${job}` : "Panel queue cleared", {
      action: { label: "Undo", onClick: undo },
    });
  };

  const importPanels = async (file) => {
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/panels/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const incoming = data.panels || [];
      if (incoming.length === 0) {
        toast.info("No panels found in that PDF.");
        return;
      }
      pushHistory();
      setPanels((prev) => {
        const seen = new Set(prev.map((p) => `${p.jobNumber}|${p.name}`));
        const fresh = incoming.filter((p) => !seen.has(`${p.jobNumber}|${p.name}`));
        return [...fresh, ...prev];
      });
      toast.success(`Imported ${incoming.length} panel${incoming.length !== 1 ? "s" : ""} from PDF`);
      const estimated = incoming.filter((p) => p.dimsEstimated);
      if (estimated.length) setReviewPanels(estimated);
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF import failed");
    } finally {
      setImporting(false);
    }
  };

  // ---- Placements ----
  const placePanel = (data) => {
    pushHistory();
    setPlacements((prev) => [...prev, { id: `place-${Date.now()}-${Math.floor(Math.random() * 999)}`, ...data }]);
  };
  const movePlacement = (id, patch) => {
    pushHistory();
    setPlacements((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };
  const rotatePlacement = (id) => {
    pushHistory();
    setPlacements((prev) => prev.map((p) => (p.id === id ? { ...p, rotated: !p.rotated } : p)));
  };
  const removePlacement = (id) => {
    pushHistory();
    setPlacements((prev) => prev.filter((p) => p.id !== id));
  };

  // ---- Loads ----
  const doAssign = (loadId, panelId) => {
    pushHistory();
    setLoads((prev) =>
      prev.map((l) =>
        l.id === loadId && !l.panelIds.includes(panelId) ? { ...l, panelIds: [...l.panelIds, panelId] } : l
      )
    );
  };
  const assignToLoad = (loadId, panelId) => {
    const load = loads.find((l) => l.id === loadId);
    if (!load || load.panelIds.includes(panelId)) return;
    const p = panelsById[panelId];
    const s = p?.strength28;
    const loadStrengths = [
      ...new Set(load.panelIds.map((id) => panelsById[id]?.strength28).filter((x) => x != null && x > 0)),
    ];
    if (s != null && s > 0 && loadStrengths.length && !loadStrengths.includes(s)) {
      toast.error(`${load.label} is ${loadStrengths.join("/")} MPa — ${p.name} is ${s} MPa`, {
        action: { label: "Add anyway", onClick: () => doAssign(loadId, panelId) },
      });
      return;
    }
    doAssign(loadId, panelId);
  };
  const unassignFromLoad = (loadId, panelId) => {
    pushHistory();
    setLoads((prev) => prev.map((l) => (l.id === loadId ? { ...l, panelIds: l.panelIds.filter((p) => p !== panelId) } : l)));
  };
  const addLoad = () => {
    pushHistory();
    setLoads((prev) => [...prev, { id: `load-${Date.now()}`, label: `Load ${prev.length + 1}`, panelIds: [] }]);
  };

  const autoPack = (job) => {
    const inLoad = new Set(loads.flatMap((l) => l.panelIds || []));
    let toPack = panels.filter((p) => !inLoad.has(p.id));
    if (job) toPack = toPack.filter((p) => (p.jobNumber || "No Job #") === job);
    toPack = toPack.sort((a, b) => (b.strength28 || 0) - (a.strength28 || 0) || (b.volume || 0) - (a.volume || 0));
    if (toPack.length === 0) {
      toast.info(job ? `No unplaced panels for ${job}` : "No unplaced panels to pack");
      return;
    }
    pushHistory();
    const newLoads = loads.map((l) => ({ ...l, panelIds: [...l.panelIds] }));
    const vols = newLoads.map((l) => l.panelIds.reduce((s, id) => s + (panelsById[id]?.volume || 0), 0));
    // each load is kept to a single 28-day strength; null = empty/unconstrained
    const mpas = newLoads.map((l) => {
      const s = l.panelIds.map((id) => panelsById[id]?.strength28).find((x) => x != null);
      return s ?? null;
    });
    let packed = 0;
    for (const p of toPack) {
      const v = p.volume || 0;
      const mpa = p.strength28 ?? null;
      let placed = false;
      for (let i = 0; i < newLoads.length; i++) {
        const mpaOk = mpas[i] == null || mpas[i] === mpa;
        if (mpaOk && vols[i] + v <= LOAD_CAPACITY + 1e-9) {
          newLoads[i].panelIds.push(p.id);
          vols[i] += v;
          if (mpas[i] == null) mpas[i] = mpa;
          placed = true;
          packed++;
          break;
        }
      }
      if (!placed) {
        newLoads.push({ id: `load-${Date.now()}-${packed}`, label: `Load ${newLoads.length + 1}`, panelIds: [p.id] });
        vols.push(v);
        mpas.push(mpa);
        packed++;
      }
    }
    setLoads(newLoads);
    toast.success(`Auto-packed ${packed} panel${packed !== 1 ? "s" : ""}${job ? ` for ${job}` : ""} into loads`);
  };

  const resetLoads = () => {
    if (!loads.some((l) => (l.panelIds || []).length)) {
      toast.info("No load assignments to clear");
      return;
    }
    pushHistory();
    setLoads((prev) => prev.map((l) => ({ ...l, panelIds: [] })));
    toast.success("Cleared all load assignments", { action: { label: "Undo", onClick: undo } });
  };

  // ---- Tables (yard layout) ----
  const applyTables = (newTables, cols) => {
    pushHistory();
    const validIds = new Set(newTables.map((t) => t.id));
    setPlacements((prev) => prev.filter((p) => validIds.has(p.table)));
    setTables(newTables);
    if (cols != null) setYardCols(cols);
    setShowTableEditor(false);
    toast.success("Yard layout updated");
  };

  const packYardAction = () => {
    const { placements: newPls, unfit } = packYard({ tables, panels, placements, panelsById, gap: 0.6, allowRotate: true });
    if (!newPls.length) {
      toast.info(unfit ? "No unplaced panels fit the tables" : "All panels are already on the yard");
      return;
    }
    pushHistory();
    const ts = Date.now();
    setPlacements((prev) => [...prev, ...newPls.map((p, i) => ({ id: `place-${ts}-${i}`, ...p }))]);
    toast.success(
      `Placed ${newPls.length} panel${newPls.length !== 1 ? "s" : ""} on the yard` + (unfit ? ` — ${unfit} didn't fit` : "")
    );
  };

  const clearTables = () => {
    if (!placements.length) {
      toast.info("The yard is already empty");
      return;
    }
    pushHistory();
    setPlacements([]);
    toast.success("Cleared all panels from the yard", { action: { label: "Undo", onClick: undo } });
  };

  const resolveOverlaps = () => {
    const tbls = tables && tables.length ? tables : makeDefaultTables();
    const clashIds = new Set();
    tbls.forEach((t) => {
      overlapIds(placements.filter((p) => p.table === t.id), panelsById).forEach((id) => clashIds.add(id));
    });
    if (!clashIds.size) {
      toast.info("No overlapping panels to resolve");
      return;
    }
    const clashPanelIds = new Set(placements.filter((p) => clashIds.has(p.id)).map((p) => p.panelId));
    const kept = placements.filter((p) => !clashIds.has(p.id));
    const panelsToReplace = panels.filter((p) => clashPanelIds.has(p.id));
    const { placements: newPls, unfit } = packYard({
      tables: tbls, panels: panelsToReplace, placements: kept, panelsById, gap: 0.6, allowRotate: true,
    });
    pushHistory();
    const ts = Date.now();
    setPlacements([...kept, ...newPls.map((p, i) => ({ id: `place-${ts}-${i}`, ...p }))]);
    toast.success(
      `Resolved ${clashIds.size} overlapping panel${clashIds.size !== 1 ? "s" : ""}` + (unfit ? ` — ${unfit} didn't fit` : ""),
      { action: { label: "Undo", onClick: undo } }
    );
  };

  const addFromBank = (bankPanels) => {
    setShowBankPicker(false);
    if (!bankPanels?.length) return;
    pushHistory();
    const ts = Date.now();
    setPanels((prev) => {
      const seen = new Set(prev.map((p) => `${p.jobNumber}|${p.name}`));
      const fresh = bankPanels
        .filter((p) => !seen.has(`${p.jobNumber}|${p.name}`))
        .map((p, i) => ({ ...p, id: `panel-${ts}-${i}` }));
      return [...fresh, ...prev];
    });
  };

  const printSchedule = () => {
    if (!panels.length && !placements.length) {
      toast.info("Nothing to print yet — add panels first.");
      return;
    }
    printDaySchedule(activeDate, { panels, placements, loads, tables, projectName, yardCols });
  };

  const isToday = activeDate === todayStr();
  const manualSave = () => {
    dirtyRef.current.add(activeDate);
    flushDirty();
  };

  const queueProps = {
    panels,
    placements,
    loads,
    importing,
    onImportFile: importPanels,
    onAdd: () => setShowForm(true),
    onEdit: (p) => setEditingPanel(p),
    onDelete: deletePanel,
    onClearAll: clearPanels,
    onAutoPackJob: autoPack,
    onOpenBank: () => setShowBankPicker(true),
    canUndo,
    onUndo: undo,
  };

  const ToolBtn = ({ testid, onClick, disabled, title, children }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="flex items-center gap-1 border border-[#2E3033] text-[#A0AABF] hover:text-white hover:border-[#2560C9] text-xs px-2.5 py-1.5 transition-colors disabled:opacity-30 disabled:hover:border-[#2E3033] disabled:hover:text-[#A0AABF]"
    >
      {children}
    </button>
  );

  return (
    <div className="h-screen flex flex-col bg-[#0A0A0A] overflow-hidden">
      <Navbar
        saveState={saveState}
        online={online}
        pending={pending}
        onSave={manualSave}
        view={view}
        onViewChange={setView}
        activeDate={activeDate}
        isToday={isToday}
        onJumpToday={() => openDate(todayStr())}
        projects={projects}
        activeProjectId={activeProjectId}
        onSwitchProject={switchProject}
        onCreateProject={createProject}
        onRenameProject={renameProject}
        onDeleteProject={deleteProject}
      />

      {view === "history" ? (
        <main data-testid="history-main" className="flex-1 overflow-y-auto p-4">
          <HistoryCalendar workspace={{ panels, placements, loads, tables, yardCols }} activeDate={activeDate} onOpenDate={openDate} projectId={activeProjectId} />
        </main>
      ) : view === "bank" ? (
        <main data-testid="bank-main" className="flex-1 overflow-y-auto p-4">
          <DrawingBank />
        </main>
      ) : view === "overview" ? (
        <main data-testid="overview-main" className="flex-1 overflow-y-auto p-4">
          <OverviewBoard onOpenYard={async (id) => { await switchProject(id); setView("planner"); }} />
        </main>
      ) : (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Toolbar: day tabs + page actions */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[#2E3033] bg-[#0A0A0A] shrink-0">
            <div data-testid="day-tabs" className="flex items-center gap-1 overflow-x-auto flex-1">
              {order.map((date) => {
                const isAct = date === activeDate;
                const isTod = date === todayStr();
                const isDirty = dirtyDates.has(date);
                const isOver = dragOverTab === date;
                return (
                  <div
                    key={date}
                    data-testid={`day-tab-${date}`}
                    draggable
                    onDragStart={(e) => {
                      dragTab.current = date;
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (dragOverTab !== date) setDragOverTab(date);
                    }}
                    onDragLeave={() => setDragOverTab((d) => (d === date ? null : d))}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragTab.current) reorderTabs(dragTab.current, date);
                      dragTab.current = null;
                      setDragOverTab(null);
                    }}
                    onDragEnd={() => {
                      dragTab.current = null;
                      setDragOverTab(null);
                    }}
                    onClick={() => setActiveDate(date)}
                    className={`group flex items-center gap-2 px-3 py-1.5 border cursor-grab active:cursor-grabbing whitespace-nowrap transition-colors ${
                      isAct ? "bg-[#2560C9] border-[#2560C9] text-white" : "border-[#2E3033] text-[#A0AABF] hover:bg-[#22252A]"
                    } ${isOver ? "border-l-2 border-l-[#F59E0B]" : ""}`}
                  >
                    {isDirty && (
                      <span
                        data-testid={`unsaved-dot-${date}`}
                        title="Unsaved changes"
                        className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] shrink-0"
                      />
                    )}
                    <span className="text-xs font-mono">{isTod ? "Today" : shortDate(date)}</span>
                    <span className={`text-[9px] font-mono ${isAct ? "text-white/70" : "text-[#6E7787]"}`}>
                      {(days[date]?.panels || []).length}p
                    </span>
                    {order.length > 1 && (
                      <span
                        data-testid={`close-tab-${date}`}
                        onClick={(e) => closeDay(e, date)}
                        className={`${isAct ? "text-white/70 hover:text-white" : "text-[#6E7787] hover:text-[#EF4444]"}`}
                      >
                        <X className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                );
              })}
              <button
                data-testid="open-day-tab"
                onClick={() => setView("history")}
                className="flex items-center gap-1 px-2.5 py-1.5 border border-dashed border-[#2E3033] text-[#6E7787] hover:text-white hover:border-[#2560C9] transition-colors"
                title="Open another day"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="flex items-center gap-1.5 shrink-0 pl-2 border-l border-[#2E3033]">
              <ToolBtn testid="undo-button" onClick={undo} disabled={!canUndo} title="Undo (Ctrl/Cmd+Z)">
                <Undo2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Undo</span>
              </ToolBtn>
              <ToolBtn testid="redo-button" onClick={redo} disabled={!canRedo} title="Redo (Ctrl/Cmd+Shift+Z)">
                <Redo2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Redo</span>
              </ToolBtn>
              <button
                data-testid="print-schedule-button"
                onClick={printSchedule}
                title="Print the whole-day schedule (yard map + panels + loads)"
                className="flex items-center gap-1 bg-[#2560C9] hover:bg-[#3B75DF] text-white text-xs px-2.5 py-1.5 transition-colors"
              >
                <Printer className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Print Schedule</span>
              </button>
            </div>
          </div>

          <div className="flex-1 flex overflow-hidden">
            <aside className="w-[300px] lg:w-[340px] shrink-0 border-r border-[#2E3033] p-3 overflow-hidden hidden md:block">
              <PanelQueue {...queueProps} />
            </aside>

            <main data-testid="dashboard-main" className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="md:hidden h-[320px]">
                <PanelQueue {...queueProps} variant="mobile" />
              </div>

              <YardPlanner
                tables={tables}
                cols={yardCols}
                panelsById={panelsById}
                placements={placements}
                onPlace={placePanel}
                onMove={movePlacement}
                onRotate={rotatePlacement}
                onRemove={removePlacement}
                onEditTables={() => setShowTableEditor(true)}
                onPackYard={packYardAction}
                onClearTables={clearTables}
                onResolveOverlaps={resolveOverlaps}
              />

              <LoadPlanner
                panelsById={panelsById}
                loads={loads}
                projectName={projectName}
                onAssign={assignToLoad}
                onUnassign={unassignFromLoad}
                onAddLoad={addLoad}
                onAutoPack={autoPack}
                onResetLoads={resetLoads}
              />
            </main>
          </div>
        </div>
      )}

      {showForm && <PanelForm onClose={() => setShowForm(false)} onCreate={addPanel} />}
      {editingPanel && (
        <PanelForm initial={editingPanel} onClose={() => setEditingPanel(null)} onUpdate={updatePanel} />
      )}
      {showTableEditor && (
        <TableEditor tables={tables} cols={yardCols} onClose={() => setShowTableEditor(false)} onSave={applyTables} />
      )}
      {reviewPanels && (
        <ImportReview panels={reviewPanels} onClose={() => setReviewPanels(null)} onConfirm={applyReviewedDims} />
      )}
      {showBankPicker && <BankPicker onClose={() => setShowBankPicker(false)} onAdd={addFromBank} />}
    </div>
  );
}
